import React, { useState } from 'react';
import './FileList.css';
import DeleteConfirmModal from './DeleteConfirmModal';

const FileList = ({ 
  documents, 
  entityType, 
  entityId, 
  onDelete,
  canDelete = true 
}) => {
  const [deletingId, setDeletingId] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getFileIcon = (fileType) => {
    if (fileType.includes('pdf')) return '📄';
    if (fileType.includes('word') || fileType.includes('document')) return '📝';
    if (fileType.includes('excel') || fileType.includes('spreadsheet')) return '📊';
    if (fileType.includes('image') || fileType.includes('jpeg') || fileType.includes('png')) return '🖼️';
    return '📎';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleDownload = async (documentId, fileName) => {
    try {
      const token = sessionStorage.getItem('token');
      const response = await fetch(
        `http://localhost:8080/api/v1/${entityType.toLowerCase()}s/${entityId}/documents/${documentId}/download`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        throw new Error('Download failed');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Download error:', error);
      alert('Failed to download file');
    }
  };

  const handleDeleteClick = (doc) => {
    setDocumentToDelete(doc);
    setShowConfirmModal(true);
  };

  const handleCancelDelete = () => {
    setShowConfirmModal(false);
    setDocumentToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!documentToDelete) return;
    const documentId = documentToDelete.id;
    setDeletingId(documentId);
    setShowConfirmModal(false);
    try {
      const token = sessionStorage.getItem('token');
      const response = await fetch(
        `http://localhost:8080/api/v1/${entityType.toLowerCase()}s/${entityId}/documents/${documentId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        throw new Error('Delete failed');
      }

      if (onDelete) {
        onDelete(documentId);
      }
    } catch (error) {
      console.error('Delete error:', error);
      alert('Failed to delete file');
    } finally {
      setDeletingId(null);
      setDocumentToDelete(null);
    }
  };

  if (!documents || documents.length === 0) {
    return (
      <div className="file-list-empty">
        <div className="empty-icon">📭</div>
        <div className="empty-text">No documents uploaded yet</div>
      </div>
    );
  }

  return (
    <div className="file-list">
      {documents.map((doc) => (
        <div key={doc.id} className="file-item">
          <div className="file-icon">
            {getFileIcon(doc.fileType)}
          </div>
          <div className="file-info">
            <div className="file-name">{doc.fileName}</div>
            <div className="file-meta">
              <span className="file-size">{formatFileSize(doc.fileSize)}</span>
              <span className="file-separator">•</span>
              <span className="file-uploader">Uploaded by {doc.uploadedByName}</span>
              <span className="file-separator">•</span>
              <span className="file-date">{formatDate(doc.uploadedAt)}</span>
            </div>
          </div>
          <div className="file-actions">
            <button
              className="file-action-btn download-btn"
              onClick={() => handleDownload(doc.id, doc.fileName)}
              title="Download"
            >
              ⬇️ Download
            </button>
            {canDelete && (
              <button
                className="file-action-btn delete-btn"
                onClick={() => handleDeleteClick(doc)}
                disabled={deletingId === doc.id}
                title="Delete"
              >
                {deletingId === doc.id ? '⏳' : '🗑️'} Delete
              </button>
            )}
          </div>
        </div>
      ))}

      <DeleteConfirmModal
        isOpen={showConfirmModal}
        title="Delete Document"
        message={<>Are you sure you want to delete the document <strong>{documentToDelete?.fileName}</strong>?</>}
        onCancel={handleCancelDelete}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
};

export default FileList;

